/**
 * Slice 45 — Supply forecast from leave-out rules × upcoming guest counts.
 * Falls back to historical avg-per-turnover when no leave-out rule is linked.
 */

import {
  computeLeaveOutQty,
  type LeaveOutRule,
} from "@/lib/leaveOut";
import { resolveLeaveOut, type LeaveOutPlan } from "@/lib/leaveOutPlan";
import { effectiveLeaveOutRule } from "@/lib/guestAmenityStaging";
import type { GuestAmenityStagingLine } from "@/types/checklist";

export type ForecastSource = "leave_out" | "past_counts";

export type ForecastStatus = "ok" | "order_soon" | "critical" | "out" | "insufficient_data";

export type UpcomingStay = {
  id: string;
  /** ISO date (YYYY-MM-DD) — turnover / arrival day */
  date: string;
  guest_count: number | null;
  guest_name: string | null;
};

export type ForecastPull = {
  stay_id: string;
  date: string;
  guest_count: number | null;
  guest_name: string | null;
  leave_qty: number;
  closet_after: number;
  estimated: boolean;
  hits_low: boolean;
};

export type SupplyForecastRow = {
  item_id: string;
  item_name: string;
  current_stock: number;
  threshold_count: number;
  source: ForecastSource;
  /** Leave-out only */
  out_next_30d: number | null;
  after: number | null;
  stay_count: number;
  order_by_label: string;
  status: ForecastStatus;
  pulls: ForecastPull[];
  /** Past-counts only */
  avg_per_turnover?: number;
  turnovers_until_empty?: number;
  predicted_runout_date?: string | null;
  submission_count?: number;
  restock_qty?: number;
};

export type LeaveOutRuleByItem = {
  item_id: string;
  rule: LeaveOutRule;
  line_label: string;
  plan?: LeaveOutPlan | null;
};

function parseDateOnly(iso: string): Date {
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
  return new Date(y!, (m ?? 1) - 1, d ?? 1);
}

function formatShortDate(iso: string): string {
  const dt = parseDateOnly(iso);
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function guestLastName(name: string | null | undefined): string | null {
  const t = (name || "").trim();
  if (!t) return null;
  const parts = t.split(/\s+/);
  return parts[parts.length - 1] || t;
}

/** Collect leave-out rules keyed by inventory_item_id (default checklist wins). */
export function collectLeaveOutRulesByItemId(
  checklists: {
    is_default?: boolean | null;
    guest_amenity_staging?: unknown;
  }[],
  parseStaging: (raw: unknown) => GuestAmenityStagingLine[],
): Map<string, LeaveOutRuleByItem> {
  const sorted = [...checklists].sort((a, b) => {
    const ad = a.is_default ? 0 : 1;
    const bd = b.is_default ? 0 : 1;
    return ad - bd;
  });
  const map = new Map<string, LeaveOutRuleByItem>();
  for (const cl of sorted) {
    const lines = parseStaging(cl.guest_amenity_staging);
    for (const line of lines) {
      const itemId = line.inventory_item_id?.trim();
      if (!itemId) continue;
      if (map.has(itemId)) continue;
      map.set(itemId, {
        item_id: itemId,
        rule: effectiveLeaveOutRule(line),
        line_label: line.item_label,
        plan: line.leave_out_plan ?? null,
      });
    }
  }
  return map;
}

/**
 * Walk upcoming stays with a leave-out rule and project closet stock.
 */
export function forecastLeaveOutItem(input: {
  item_id: string;
  item_name: string;
  current_stock: number;
  threshold_count: number;
  rule: LeaveOutRule;
  plan?: LeaveOutPlan | null;
  stays: UpcomingStay[];
}): SupplyForecastRow {
  const now = Math.max(0, Math.floor(input.current_stock));
  const threshold = Math.max(0, Math.floor(input.threshold_count));
  const pulls: ForecastPull[] = [];
  let stock = now;
  let outTotal = 0;
  let orderByLabel = "OK";
  let firstHit: ForecastPull | null = null;
  const alreadyLow = now <= threshold;

  if (alreadyLow && now === 0) {
    orderByLabel = "Out";
  } else if (alreadyLow) {
    const next = input.stays[0];
    if (next) {
      orderByLabel = `now · before ${guestLastName(next.guest_name) ?? "next"} stay ${formatShortDate(next.date)}`;
    } else {
      orderByLabel = "Order now";
    }
  }

  for (const stay of input.stays) {
    let leave_qty: number;
    let used_minimum: boolean;
    if (input.plan) {
      const planned = resolveLeaveOut(input.plan, stay.guest_count);
      leave_qty = planned?.leaveQty ?? 0;
      used_minimum = planned?.usedSmallest ?? false;
    } else {
      const legacy = computeLeaveOutQty(stay.guest_count, input.rule);
      leave_qty = legacy.leave_qty;
      used_minimum = legacy.used_minimum;
    }
    const before = stock;
    const after = Math.max(0, stock - leave_qty);
    outTotal += leave_qty;
    const hits_low = !alreadyLow && before > threshold && after <= threshold;
    const pull: ForecastPull = {
      stay_id: stay.id,
      date: stay.date,
      guest_count: stay.guest_count,
      guest_name: stay.guest_name,
      leave_qty,
      closet_after: after,
      estimated: used_minimum,
      hits_low,
    };
    pulls.push(pull);
    if (hits_low && !firstHit) {
      firstHit = pull;
      const who = guestLastName(stay.guest_name);
      orderByLabel = who
        ? `${formatShortDate(stay.date)} · after ${who} stay`
        : `${formatShortDate(stay.date)} · after stay`;
    }
    stock = after;
  }

  let status: ForecastStatus = "ok";
  if (now === 0) status = "out";
  else if (alreadyLow) status = "critical";
  else if (firstHit) {
    const idx = pulls.findIndex((p) => p.stay_id === firstHit!.stay_id);
    status = idx <= 0 ? "critical" : idx <= 2 ? "order_soon" : "order_soon";
    // If low hit is within first stay → critical; else order_soon when within window
    if (idx === 0) status = "critical";
  } else if (stock <= threshold && input.stays.length > 0) {
    status = "order_soon";
  }

  return {
    item_id: input.item_id,
    item_name: input.item_name,
    current_stock: now,
    threshold_count: threshold,
    source: "leave_out",
    out_next_30d: outTotal,
    after: stock,
    stay_count: input.stays.length,
    order_by_label: status === "ok" && !alreadyLow ? "OK" : orderByLabel,
    status,
    pulls,
  };
}

/** Map historical RPC row into SupplyForecastRow (past counts). */
export function forecastFromPastCounts(input: {
  item_id: string;
  item_name: string;
  current_stock: number;
  threshold_count: number;
  avg_per_turnover: number;
  turnovers_until_empty: number;
  predicted_runout_date: string | null;
  submission_count: number;
  restock_qty: number;
  status: ForecastStatus;
  upcoming_turnovers_30d: number;
}): SupplyForecastRow {
  const avg = Number(input.avg_per_turnover) || 0;
  const out =
    input.submission_count >= 2 && avg > 0
      ? Math.round(avg * input.upcoming_turnovers_30d)
      : null;
  const after =
    out != null ? Math.max(0, input.current_stock - out) : null;

  let orderBy = "OK";
  if (input.status === "out") orderBy = "Out";
  else if (input.status === "insufficient_data") orderBy = "Need more data";
  else if (input.predicted_runout_date) {
    orderBy = `~${formatShortDate(input.predicted_runout_date)}`;
  } else if (input.status === "critical" || input.status === "order_soon") {
    orderBy = "Order soon";
  }

  return {
    item_id: input.item_id,
    item_name: input.item_name,
    current_stock: input.current_stock,
    threshold_count: input.threshold_count,
    source: "past_counts",
    out_next_30d: out,
    after,
    stay_count: input.upcoming_turnovers_30d,
    order_by_label: orderBy,
    status: input.status,
    pulls: [],
    avg_per_turnover: avg,
    turnovers_until_empty: input.turnovers_until_empty,
    predicted_runout_date: input.predicted_runout_date,
    submission_count: input.submission_count,
    restock_qty: input.restock_qty,
  };
}

export function formatPullLine(pull: ForecastPull): string {
  const date = formatShortDate(pull.date);
  if (pull.estimated || pull.guest_count == null) {
    return `Guests not set → leave ${pull.leave_qty} · ${date}`;
  }
  return `${pull.guest_count} guests → leave ${pull.leave_qty} · ${date}`;
}

export function statusSortKey(status: ForecastStatus): number {
  switch (status) {
    case "out":
      return 0;
    case "critical":
      return 1;
    case "order_soon":
      return 2;
    case "ok":
      return 3;
    default:
      return 4;
  }
}

export const FORECAST_ATTENTION: ForecastStatus[] = ["out", "critical", "order_soon"];

export function formatBannerLine(row: SupplyForecastRow): string {
  if (row.status === "out") return `Order ${row.item_name} — out of stock`;
  if (row.source === "leave_out" && row.pulls.length > 0) {
    const hitIdx = row.pulls.findIndex((p) => p.hits_low);
    const staysLeft =
      hitIdx >= 0 ? hitIdx + 1 : Math.max(1, row.pulls.length);
    return `Order ${row.item_name} soon — ${staysLeft} stay${staysLeft !== 1 ? "s" : ""} left before low`;
  }
  if (row.turnovers_until_empty != null && row.turnovers_until_empty < 999) {
    const n = row.turnovers_until_empty;
    return `Order ${row.item_name} soon — ~${n} turnover${n !== 1 ? "s" : ""} left`;
  }
  return `Order ${row.item_name} soon`;
}
